import {
  Alert,
  AlertType,
  AlertsCommentary,
  alertHasResolvedComment,
  alertShouldShowInReferencePanel,
  getAlertCompType,
  getAlertTypeLabel,
  getOrderedAlertPageGroups,
  nonDismissibleAlertTypes,
  expected,
  normalizePairedCostRouteItem,
} from '@aloft/toolkit-shared';
import { Button, Flex, Icon, Input, Tooltip, Typography, useThemeColor } from '@aloft/toolkit-sui';

import { debounce, isEqual } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, FormProvider, useFormContext, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { CompTypeBadge } from '../../../components/CompTypeBadge.tsx';
import {
  getPropertyIdFromPropertyGridCellKey,
  parsePropertyGridCellKey,
} from '../../../components/PropertyGrid/types.ts';
import { dismissAlertToasts } from '../../../hooks/useToaster/dismissAllToasts.ts';
import { useZodForm } from '../../../hooks/useZodForm.ts';
import {
  APPRAISIFY_FIX_DESTINATION_BLOCKED_TOOLTIP,
  routeToStepMap,
} from '../../../hooks/validation/consts.ts';
import { useAppraisifyNavigation } from '../../../hooks/validation/useAppraisifyNavigation.ts';
import { cancelDismissAlert, dismissAlert } from '../../../redux/features/alertsSlice.ts';
import { setCommentaryV2 } from '../../../redux/features/commentarySlice.ts';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectIsCommentaryLoaded } from '../../../redux/features/selectors/commentarySelectors/selectIsCommentaryLoaded.ts';
import { selectIsInspectifyOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectTriggeredAlerts } from '../../../redux/features/selectors/uiSelectors.ts';
import { selectReferenceBarUI } from '../../../redux/features/selectors/referenceBarUISelectors.ts';
import { setActiveID, setFocusedAlertType } from '../../../redux/features/referenceBarUISlice.ts';
import {
  setFocusedOrderDetailsFieldKey,
  setFocusedPropertyGridCellKey,
} from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { AlertResolvedIcon } from '../../alerts/AlertResolvedIcon.tsx';
import { ALERT_SEVERITY_ICON_PROPS } from '../../alerts/alertSeverityIconProps.ts';
import {
  focusCostApproachSliders,
  isCostApproachAlertFixRoute,
} from '../../alerts/costApproachSliderFocus.ts';
import {
  shouldPreserveAlertHoverPreview,
  syncAlertHoverPreview,
} from '../../alerts/syncAlertHoverPreview.ts';
import { parseOrderDetailsTargetCellKey } from '../../orderDetails/orderDetailsFieldFocus.ts';
import { propertyIdToReferenceViewId } from '../index.ts';
import { getAlertPageLabel } from './getAlertPageLabel.ts';
import { useAlerts } from './hooks/useAlerts.tsx';

import './AlertsContent.scss';

const scrollFocusedAlertIntoView = (element: HTMLElement) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  });
};

const AlertItem = ({ alert }: { alert: Alert }) => {
  const {
    type,
    status,
    message,
    propertyID,
    fixingInfo: { tips, pairedCostRouteItem, fixingMethod, targetCellKey, commentAlwaysRequired },
  } = alert;
  const alertCompType = getAlertCompType(alert);

  let { severity } = alert;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { isRouteValidationStepEnabled } = useAppraisifyNavigation();

  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const { focusedAlertType, hoveredAlertType } = useAppSelector(selectReferenceBarUI);
  const isFocused = focusedAlertType === type;
  const isHoveredFromAlert = hoveredAlertType === type;
  const isFocusedToastHovered = isFocused && isHoveredFromAlert;
  const itemRef = useRef<HTMLDivElement>(null);

  const { control } = useFormContext<AlertsCommentary>();
  const commentValue = useWatch({ control, name: type });
  const hasResolvedComment = alertHasResolvedComment(
    alert,
    commentValue != null && String(commentValue).trim() !== ''
      ? { [type]: String(commentValue) }
      : undefined,
  );

  useEffect(() => {
    if (!isFocused || !itemRef.current) {
      return;
    }
    scrollFocusedAlertIntoView(itemRef.current);
  }, [isFocused]);

  useEffect(() => {
    const element = itemRef.current;
    if (!element) {
      return;
    }
    const handleMouseLeave = (event: MouseEvent) => {
      if (element.contains(event.relatedTarget as Node)) {
        return;
      }
      if (shouldPreserveAlertHoverPreview(event.relatedTarget as HTMLElement | null)) {
        return;
      }
      syncAlertHoverPreview(dispatch, null);
    };
    element.addEventListener('mouseleave', handleMouseLeave);
    return () => element.removeEventListener('mouseleave', handleMouseLeave);
  }, [dispatch]);

  const isDismissed = status === 'dismissed';
  const isNonDismissable = nonDismissibleAlertTypes.includes(type);
  const canDismiss = !isNonDismissable;
  const hasAction = fixingMethod.includes('action');
  const hasComment = fixingMethod.includes('comment');
  const showFixNow = hasAction && isInspectifyOrder && pairedCostRouteItem && status === 'active';
  const fixDestinationRoute = normalizePairedCostRouteItem(pairedCostRouteItem, routeToStepMap);
  const isFixDestinationAccessible =
    !fixDestinationRoute || isRouteValidationStepEnabled(fixDestinationRoute);
  const showIgnore =
    !isDismissed && canDismiss && severity === 'warning' && hasComment && isInspectifyOrder;
  const showExplain =
    !isDismissed && canDismiss && severity === 'error' && hasComment && isInspectifyOrder;
  const isAlertItemClickable = Boolean(showFixNow && isFixDestinationAccessible);

  const focusCostApproachSlidersIfNeeded = () => {
    if (
      propertyID &&
      !targetCellKey &&
      isCostApproachAlertFixRoute(fixDestinationRoute ?? pairedCostRouteItem)
    ) {
      focusCostApproachSliders(dispatch, propertyID);
    }
  };

  const focusAlertTarget = (activate = false) => {
    if (!targetCellKey) {
      focusCostApproachSlidersIfNeeded();
      return;
    }
    const orderDetailsField = parseOrderDetailsTargetCellKey(targetCellKey);
    if (orderDetailsField) {
      dispatch(setFocusedOrderDetailsFieldKey(orderDetailsField));
      return;
    }
    const parsedCellKey = parsePropertyGridCellKey(targetCellKey);
    if (parsedCellKey) {
      const propertyId = getPropertyIdFromPropertyGridCellKey(parsedCellKey);
      if (propertyId !== 'subject') {
        dispatch(setActiveID(propertyIdToReferenceViewId(propertyId)));
      }
      dispatch(
        activate
          ? setFocusedPropertyGridCellKey({ key: parsedCellKey, activate: true })
          : setFocusedPropertyGridCellKey(parsedCellKey),
      );
    }
  };

  const focusAlert = () => {
    dispatch(setFocusedAlertType(type));
    dismissAlertToasts();
    focusAlertTarget();
  };

  const navigateToAlertFix = () => {
    dispatch(setFocusedAlertType(type));
    dismissAlertToasts();
    focusAlertTarget(true);
    navigate(`/order/${expected(orderId)}/paired-cost/${pairedCostRouteItem}`);
  };

  const handleFixNow = () => navigateToAlertFix();

  if (!isInspectifyOrder && severity === 'error') {
    severity = 'warning';
  }

  const itemClassName = [
    'alerts-sheet-alert-item',
    isAlertItemClickable ? 'alerts-sheet-alert-item--clickable' : undefined,
    isFocused ? 'alerts-sheet-alert-item--focused' : undefined,
    isFocused ? `alerts-sheet-alert-item--${severity}` : undefined,
    isFocusedToastHovered ? 'alerts-sheet-alert-item--focused-toast-hovered' : undefined,
    isHoveredFromAlert && !isFocused ? 'alerts-sheet-alert-item--hovered' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Flex
      ref={itemRef}
      key={type}
      className={itemClassName}
      flexDirection="column"
      gap="8px"
      alignItems="flex-end"
      width="100%"
      padding="4px"
      boxSizing="border-box"
      onClick={isAlertItemClickable ? focusAlert : undefined}
      onMouseEnter={() => syncAlertHoverPreview(dispatch, alert)}
    >
      <Flex gap="8px" alignItems="flex-start" width="100%">
        {hasResolvedComment ? (
          <AlertResolvedIcon />
        ) : (
          <Icon size="lg" {...ALERT_SEVERITY_ICON_PROPS[severity]} />
        )}
        <Flex flex={1} minWidth={0} flexDirection="column" gap="4px">
          <Flex alignItems="center" gap="8px" flexWrap="wrap">
            <Typography variant="h4">{getAlertTypeLabel(type)}</Typography>
            {alertCompType && <CompTypeBadge compType={alertCompType} />}
          </Flex>
          <Typography variant="body1">{message}</Typography>
        </Flex>
      </Flex>

      <Flex flexDirection="column" width="100%" paddingLeft="32px">
        <Typography variant="body2" color="surfaceAccent4">
          {tips}
        </Typography>
      </Flex>

      {(isDismissed || commentAlwaysRequired) && (
        <Controller
          name={type}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Flex
              width="100%"
              flexDirection="column"
              gap="4px"
              paddingLeft="32px"
              onClick={(event) => event.stopPropagation()}
            >
              <Input
                type="multiline"
                resize="vertical"
                __dangerDoNotUseSx={
                  error ? { borderColor: useThemeColor('alertError') } : undefined
                }
                value={value}
                onClear={() => onChange('')}
                onChange={onChange}
                placeholderText="Text entered here will appear in the final report. Please enter accurate, professional content."
              />
              <Flex justifyContent="space-between" alignItems="center" width="100%">
                {error ? (
                  <Typography variant="body2" color="alertError">
                    Required
                  </Typography>
                ) : (
                  <Flex />
                )}
                {canDismiss && !commentAlwaysRequired && (
                  <Button
                    variant="inline"
                    size="sm"
                    text="Cancel"
                    onClick={() => dispatch(cancelDismissAlert(type))}
                  />
                )}
              </Flex>
            </Flex>
          )}
        />
      )}

      {!isDismissed && (showFixNow || showIgnore || showExplain) && (
        <Flex
          width="100%"
          gap="8px"
          justifyContent="flex-end"
          onClick={(event) => event.stopPropagation()}
        >
          {showFixNow &&
            (isFixDestinationAccessible ? (
              <Button size="sm" text="Fix Now" onClick={handleFixNow} />
            ) : (
              <Tooltip
                label={APPRAISIFY_FIX_DESTINATION_BLOCKED_TOOLTIP}
                placement="bottom"
                hasArrow
                openDelay={200}
                bg="textPrimary"
                color="textOffset"
              >
                <Button size="sm" text="Fix Now" disabled onClick={() => undefined} />
              </Tooltip>
            ))}
          {showIgnore && (
            <Button
              variant="secondary"
              size="sm"
              text="Ignore"
              onClick={() => dispatch(dismissAlert(type))}
            />
          )}
          {showExplain && (
            <Button
              variant="secondary"
              size="sm"
              text="Explain"
              onClick={() => dispatch(dismissAlert(type))}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};

export const AlertsContent = ({ alerts }: { alerts: ReturnType<typeof useAlerts> }) => {
  const dispatch = useAppDispatch();
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const triggeredAlertTypes = useAppSelector(selectTriggeredAlerts);

  const commentaryV2 = useAppSelector(selectCommentaryV2);
  const isCommentaryLoaded = useAppSelector(selectIsCommentaryLoaded);
  const alertsCommentary = commentaryV2.alerts;

  const pageGroups = useMemo(() => {
    const groups = getOrderedAlertPageGroups(alerts, routeToStepMap);
    if (!isInspectifyOrder) {
      return groups;
    }
    return groups
      .map(({ page, alerts: pageAlerts }) => ({
        page,
        alerts: pageAlerts.filter((alert) =>
          alertShouldShowInReferencePanel(alert, triggeredAlertTypes),
        ),
      }))
      .filter(({ alerts: pageAlerts }) => pageAlerts.length > 0);
  }, [alerts, isInspectifyOrder, triggeredAlertTypes]);

  const dismissedAlertTypes = useMemo(
    () =>
      Object.values(alerts)
        .flat()
        .filter(({ status }) => status === 'dismissed' || status === 'resolved_pending_comment')
        .map(({ type }) => type),
    [alerts],
  );

  const schDismissedAlertsCommentary = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          dismissedAlertTypes.map((type) => [type, z.string().min(5, 'This field is required')]),
        ),
      ),
    [dismissedAlertTypes],
  );

  const zodForm = useZodForm({
    schema: schDismissedAlertsCommentary,
    defaultValues: alertsCommentary ?? {},
    mode: 'all',
  });

  const { watch, reset } = zodForm;

  useEffect(() => {
    if (!isCommentaryLoaded) {
      return;
    }
    reset(alertsCommentary ?? {});
  }, [alertsCommentary, isCommentaryLoaded, reset]);

  const debouncedSetCommentary = useMemo(
    () =>
      debounce((newAlertsCommentary: AlertsCommentary | undefined) => {
        if (isEqual(newAlertsCommentary, commentaryV2.alerts)) return;

        dispatch(
          setCommentaryV2({
            ...commentaryV2,
            alerts: newAlertsCommentary,
          }),
        );
      }, 300),
    [dispatch, commentaryV2],
  );

  useEffect(() => {
    zodForm.trigger();
  }, [dismissedAlertTypes, zodForm]);

  useEffect(() => {
    const subscription = watch((formValues) => {
      const cleanedFormData = Object.entries(formValues)
        .map(([k, v]) => [k, v === '' ? undefined : v?.trim()])
        .filter(([_k, v]) => v !== undefined) as [AlertType, string][];

      debouncedSetCommentary(
        cleanedFormData.length ? Object.fromEntries(cleanedFormData) : undefined,
      );
    });

    return () => subscription.unsubscribe();
  }, [dispatch, watch, debouncedSetCommentary]);

  if (pageGroups.length === 0) {
    return <Typography>🎉 Congrats, no alerts remain!</Typography>;
  }

  return (
    <FormProvider {...zodForm}>
      {pageGroups.map(({ page, alerts: pageAlerts }) => (
        <Flex key={page} flexDirection="column" gap="16px" width="100%">
          <Typography variant="h3">{getAlertPageLabel(page)}</Typography>
          <Flex flexDirection="column" gap="16px" width="100%">
            {pageAlerts.map((alert) => (
              <AlertItem key={alert.type} alert={alert} />
            ))}
          </Flex>
        </Flex>
      ))}
    </FormProvider>
  );
};
